import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { scoringAPI } from '../lib/api';
import { apiError } from '../components/fleet/fleetUi';

/**
 * Scoring: the workspace's climbing competitions. Each one runs in Mission
 * Control (screens at /scoring/<id>/judge and so on) or on a laptop at the
 * venue that took it down with a link code and pushes everything back.
 */
export default function ScoringPage() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [presets, setPresets] = useState({});
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [preset, setPreset] = useState('bcs-grand-final');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    scoringAPI.list()
      .then((res) => { setList(res.data.competitions); setPresets(res.data.presets || {}); })
      .catch((err) => setError(apiError(err, 'Could not load competitions')));
  }, []);

  async function create(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await scoringAPI.create({ name: name.trim(), preset });
      navigate(`/scoring/${res.data.competition.id}`);
    } catch (err) {
      setError(apiError(err, 'Could not create the competition'));
      setBusy(false);
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-app-fg">Scoring</h1>
          <p className="text-sm text-gray-500 mt-0.5">Climbing competitions: judges, head judge, holding monitor and a live Singular feed, in Mission Control or on a laptop at the venue.</p>
        </div>
        {!creating && (
          <button onClick={() => setCreating(true)} className="px-3 py-1.5 text-sm rounded font-medium text-white" style={{ background: 'var(--blue)' }}>New competition</button>
        )}
      </div>

      {error && <div className="mb-4 px-3 py-2 rounded text-sm" style={{ color: 'var(--live)', background: 'var(--live-bg)' }}>{error}</div>}

      {creating && (
        <form onSubmit={create} className="mb-5 rounded-lg border border-app-line-chrome bg-app-well p-4 space-y-4">
          <label className="block text-sm space-y-1">
            <span className="text-gray-400">Name</span>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. BCS Grand Final 2026" className="w-full px-3 py-2 rounded bg-app-canvas border border-app-line-chrome text-app-fg" />
          </label>
          <fieldset className="space-y-2">
            <legend className="text-sm text-gray-400 mb-1">Format</legend>
            {Object.entries(presets).map(([key, p]) => (
              <label key={key} className={`flex gap-3 rounded border px-3 py-2 cursor-pointer ${preset === key ? 'border-blue-500' : 'border-app-line-chrome'}`}>
                <input type="radio" name="preset" checked={preset === key} onChange={() => setPreset(key)} className="mt-1" />
                <span>
                  <span className="block text-sm font-medium text-app-fg">{p.label}</span>
                  <span className="block text-xs text-gray-500">{p.description}</span>
                </span>
              </label>
            ))}
            <p className="text-xs text-gray-500">Every rule can be changed afterwards in the head judge&apos;s Setup.</p>
          </fieldset>
          <div className="flex gap-2">
            <button disabled={!name.trim() || busy} className="px-4 py-2 text-sm rounded font-medium text-white disabled:opacity-50" style={{ background: 'var(--blue)' }}>{busy ? 'Creating…' : 'Create'}</button>
            <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 text-sm rounded text-gray-400 hover:text-app-fg">Cancel</button>
          </div>
        </form>
      )}

      {list === null && !error && <div className="text-center py-16 text-gray-600">Loading…</div>}
      {list?.length === 0 && !creating && (
        <div className="text-center py-16 text-gray-500 text-sm">No competitions yet.</div>
      )}
      <div className="space-y-2">
        {(list || []).map((c) => (
          <Link key={c.id} to={`/scoring/${c.id}`} className="flex items-center gap-4 rounded-lg border border-app-line-chrome bg-app-well hover:border-gray-500 px-5 py-4">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-app-fg truncate">{c.name}</div>
              <div className="text-xs text-gray-500 mt-0.5">{c.events} events · updated {new Date(c.updated_at).toLocaleString()}</div>
            </div>
            <ModeBadge mode={c.mode} />
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ModeBadge({ mode }) {
  return (
    <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${mode === 'venue' ? 'bg-amber-500/20 text-amber-300' : 'bg-sky-500/20 text-sky-300'}`}>
      {mode === 'venue' ? 'At the venue' : 'In the cloud'}
    </span>
  );
}
